import { css, type Theme } from '@emotion/react';
import styled from '@emotion/styled';
import { sdsColor } from '@staging-design-system';
import { rgba } from '@utils';

const Layout = styled.div<{
  hasDarkMode?: boolean;
}>(
  ({ hasDarkMode, theme }) => `
  --mobile-sidebar-topbar-height: 64px;
  background: ${theme.palette.common.white};
  contain: inline-size;
  display: grid;
  grid-template: auto 1fr / auto 1fr auto;
  grid-template-areas: 'c b b' 'c d e';
  height: 100dvh;
  width: 100%;

  @media (max-width: 768px) {
    grid-template: auto auto 1fr / 1fr;
    grid-template-areas: 'c' 'b' 'd';
  }

  ${
    hasDarkMode &&
    `
      @media (prefers-color-scheme: dark) {
        background: ${theme.palette.common.black};
      }
    `
  } 
`
);

export default { Layout };

export const getColorScheme = (hasDarkMode: boolean) => (theme: Theme) =>
  css`
    :root {
      // --------------------------------------------
      // Appearence (color scheme)
      // --------------------------------------------
      // Assume that 'light' is the device default
      // --------------------------------------------

      --sidebar-box-shadow: 0 2px 4px 0 rgba(25, 21, 48, 0.08);
      
      --action-panel-background-color: ${theme.palette.common.white};
      --action-panel-title-color: ${theme.palette.common.black};
      --action-panel-button-color: ${theme.palette.common.black};
      --action-panel-icon-color: ${theme.palette.common.black};
      --action-panel-border-bottom-color: ${theme.palette.grey['200']};
      --main-color: ${theme.palette.common.black};
      --main-background-color: transparent;
      --main-input-wrapper-background-color: ${theme.palette.common.white};
      --main-input-background-color: ${theme.palette.common.white};
      --main-input-color: ${theme.palette.common.black};
      --main-input-icon-color: ${theme.palette.common.black};
      --insight-list-background-color: ${theme.palette.common.white};
      --insight-list-action-group-border-color: ${theme.palette.grey['200']};
      --insight-list-item-background-color: ${theme.palette.common.white};
      --insight-list-item-active-background-color: ${rgba(
        theme.palette.pink.medium,
        0.15
      )};
      --insight-list-item-color: ${theme.palette.common.black};
      --insight-list-chart-background-color: ${theme.palette.common.white};
      --insight-action-list-background-color: ${theme.palette.common.white};
      --insight-visualization-title-color: ${theme.palette.common.black};
      --insight-visualization-table-border-color: ${theme.palette.grey['200']};
      --insight-visualization-caption-color: ${theme.palette.grey['600']};
      --insight-visualization-notice-color: ${theme.palette.grey['600']};
      --sidebar-background-color: ${sdsColor.background.neutralDefault};
      --sidebar-color: ${sdsColor.text.defaultDefault};
      --menu-group-item-highlight-background-color: ${theme.palette.pink.bg};
      --right-panel-background-color: ${theme.palette.grey['100']};
      --right-panel-color: ${theme.palette.common.black};
      --right-panel-border-left-color: ${theme.palette.grey['200']};
      --right-panel-list-item-active-background-color: ${
        theme.palette.grey['100']
      };
      --right-panel-list-item-active-color: ${theme.palette.common.black};
      --right-panel-list-item-active-box-shadow-color: ${
        theme.palette.grey['200']
      };
      --right-panel-list-item-box-shadow-color: ${theme.palette.grey['200']};
      --navbar-border-color: ${theme.palette.grey['200']};
      --navbar-background-color: ${theme.palette.grey['100']};
      --navbar-title-color: ${theme.palette.common.black};
      --user-agreement-dialog-link-background-color: ${
        theme.palette.common.black
      };
      --card-background-color: ${theme.palette.common.white};
      --card-color: ${theme.palette.common.black};
      --card-disabled-background-color: ${theme.palette.grey['100']};
      --card-disabled-border-color: ${theme.palette.grey['200']};
      --card-disabled-color: ${theme.palette.common.black};
      --chat-message-echo-background-color: ${theme.palette.grey['100']};
      --chat-message-echo-mask-color: ${rgba(theme.palette.pink.medium, 0.15)};
      --chat-message-color: ${theme.palette.common.black};
      --chart-non-highlighted-opacity: 0.4;
      --dropdown-menu-icon-background-color: ${theme.palette.common.white};
      --dropdown-menu-icon-color: ${theme.palette.common.black};
      --expand-button-background-color: ${theme.palette.common.white};
      --expand-button-color: ${theme.palette.common.black};
      --expand-button-line-color: ${theme.palette.grey['200']};
      --expand-button-box-shadow-color: ${theme.palette.grey['200']};
      --source-overview-item-background-color: ${theme.palette.common.white};
      --source-overview-item-color: ${theme.palette.common.black};
      --collapsible-panel-header-color: ${theme.palette.grey['600']};
      --collapsible-panel-content-border-color: ${theme.palette.grey['300']};
      --avatar-reply-background-color: ${theme.palette.common.black};
      --avatar-reply-color: ${theme.palette.common.white};
      --chart-grid-color: ${theme.palette.grey['200']};
      --compact-chart-tick-color: ${theme.palette.grey['600']};
      --feedback-content-background-color: ${theme.palette.common.white};
      --prompt-status-background-color: ${theme.palette.grey['100']};
      --prompt-status-color: ${theme.palette.grey['600']};
      --prompt-cta-color: ${theme.palette.violet.medium};
      --experimental-feature-box-shadow-color: ${theme.palette.grey['200']};
      --experimental-feature-color: ${theme.palette.common.black};
      --summary-background-color: ${theme.palette.grey['100']};
      --summary-color: ${theme.palette.violet.medium};
      --summary-alternative-color: ${theme.palette.violet.dark};
      --summary-action-color: ${theme.palette.common.black};

      ${
        hasDarkMode
          ? `
    @media (prefers-color-scheme: dark) {
      --action-panel-background-color: ${theme.palette.common.black};
      --action-panel-title-color: ${theme.palette.common.white};
      --action-panel-button-color: ${theme.palette.common.white};
      --action-panel-icon-color: ${theme.palette.common.white};
      --action-panel-border-bottom-color: ${theme.palette.grey['600']};
      --main-color: ${theme.palette.common.white};
      --main-background-color: transparent;
      --main-input-wrapper-background-color: ${theme.palette.common.black};
      --main-input-background-color: ${theme.palette.common.black};
      --main-input-color: ${theme.palette.common.white};
      --main-input-icon-color: ${theme.palette.common.white};
      --insight-list-background-color: ${theme.palette.common.black};
      --insight-list-action-group-border-color: ${theme.palette.grey['500']};
      --insight-list-item-background-color: ${theme.palette.common.black};
      --insight-list-item-active-background-color: rgba(108, 47, 88, 0.3);
      --insight-list-item-color: ${theme.palette.common.white};
      --insight-list-chart-background-color: ${theme.palette.common.black};
      --insight-action-list-background-color: ${theme.palette.common.black};
      --insight-visualization-title-color: ${theme.palette.common.white};
      --insight-visualization-table-border-color: ${theme.palette.grey['600']};
      --insight-visualization-caption-color: ${theme.palette.grey['500']};
      --insight-visualization-notice-color: ${theme.palette.grey['100']};
      --sidebar-background-color: ${theme.palette.common.black};
      --sidebar-color: ${theme.palette.common.white};
      --menu-group-item-highlight-background-color: rgb(47, 30, 58);
      --right-panel-background-color: ${theme.palette.grey['600']};
      --right-panel-color: ${theme.palette.common.white};
      --right-panel-border-left-color: ${theme.palette.grey['500']};
      --right-panel-list-item-active-background-color: rgb(57, 61, 95);
      --right-panel-list-item-active-color: ${theme.palette.grey['400']};
      --right-panel-list-item-active-box-shadow-color: ${theme.palette.grey['500']};
      --right-panel-list-item-box-shadow-color: ${theme.palette.grey['600']};
      --navbar-border-color: ${theme.palette.grey['500']};
      --navbar-background-color: ${theme.palette.common.black};
      --navbar-title-color: ${theme.palette.common.white};
      --user-agreement-dialog-link-background-color: ${theme.palette.common.white};
      --card-color: ${theme.palette.common.white};
      --card-background-color: ${theme.palette.common.black};
      --card-disabled-background-color: rgb(57, 61, 95);
      --card-disabled-border-color: ${theme.palette.grey['500']};
      --card-disabled-color: ${theme.palette.grey['500']};
      --chat-message-echo-background-color: ${theme.palette.grey['600']};
      --chat-message-echo-mask-color: rgba(108, 47, 88, 0.3);
      --chat-message-color: ${theme.palette.common.white};
      --chart-non-highlighted-opacity: 0.7;
      --dropdown-menu-icon-background-color: ${theme.palette.common.black};
      --dropdown-menu-icon-color: ${theme.palette.common.white};
      --expand-button-background-color: ${theme.palette.common.black};
      --expand-button-color: ${theme.palette.common.white};
      --expand-button-line-color: ${theme.palette.grey['600']};
      --expand-button-box-shadow-color: ${theme.palette.grey['600']};
      --source-overview-item-background-color: ${theme.palette.common.black};
      --source-overview-item-color: ${theme.palette.common.white};
      --collapsible-panel-header-color: ${theme.palette.grey['500']};
      --collapsible-panel-content-border-color: ${theme.palette.grey['500']};
      --avatar-reply-background-color: ${theme.palette.common.white};
      --avatar-reply-color: ${theme.palette.common.black};
      --chart-grid-color: ${theme.palette.grey['600']};
      --compact-chart-tick-color: ${theme.palette.grey['400']};
      --feedback-content-background-color: ${theme.palette.common.black};
      --prompt-status-background-color: rgb(57, 61, 95);
      --prompt-status-color: ${theme.palette.common.white};
      --prompt-cta-color: ${theme.palette.pink.light};
      --experimental-feature-box-shadow-color: ${theme.palette.grey['500']};
      --experimental-feature-color: ${theme.palette.common.white};
      --summary-background-color: rgb(57, 61, 95);
      --summary-color: ${theme.palette.violet.bg};
      --summary-alternative-color: ${theme.palette.violet.bg};
      --summary-action-color: ${theme.palette.common.white};

      .static-scrollbar::-webkit-scrollbar-track {
        background: rgb(57, 61, 95);
        border: 3px solid rgb(57, 61, 95);
        border-radius: 6px;
      }

      .static-scrollbar::-webkit-scrollbar-thumb {
        background: ${theme.palette.grey['500']};
        border-radius: 6px;
        border: 3px solid rgb(57, 61, 95);
      }      
    }`
          : null
      }
    }
  `;
